import { useState } from 'react'
import { AlertCircle, CheckCircle2, Loader2, Sparkles } from 'lucide-react'
import { MockupGalleryDialog } from 'src/pages/_myAccount/Requests/Request/components/tabs/OverviewTab/MockupGalleryDialog'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Image } from 'src/shared/components/ui'
import { Progress } from 'src/shared/components/ui/progress'
import { Skeleton } from 'src/shared/components/ui/skeleton'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { cn } from 'src/shared/components/ui/utils'

type MockupResult = {
  quote_item_id: number
  quote_sub_item_id: number
  product_name: string
  product_image_url?: string
  mockup_urls: string[]
  success: boolean
  pending?: boolean
  error?: string
}

export const MockupGenerationMessage = ({
  content,
}: {
  content: {
    message?: string
    success?: boolean
    skipped?: boolean
    pending?: boolean
    data?: {
      results?: MockupResult[]
    }
  }
}) => {
  const { t } = useLocalization()
  const [galleryResult, setGalleryResult] = useState<MockupResult | null>(null)
  const results = content.data?.results ?? []
  const isSkipped = content.skipped === true
  const isPending =
    content.pending === true || results.some((result) => result.pending)
  const readyCount = results.filter(
    (result) => result.success && (result.mockup_urls?.length ?? 0) > 0
  ).length
  const progress =
    results.length > 0 ? Math.round((readyCount / results.length) * 100) : 0

  const title = isSkipped
    ? t(content.message)
    : isPending
      ? results.length > 1
        ? t(
            `Generating mockups — ${readyCount} of ${results.length} ready`,
            `Genererar mockups — ${readyCount} av ${results.length} klara`
          )
        : t('Generating mockup…', 'Genererar mockup…')
      : t(content.message)

  return (
    <>
    <Column
      className={cn(
        'gap-2 rounded-lg border px-3 py-2',
        isSkipped
          ? 'border-amber-200 bg-amber-50'
          : 'border-violet-200 bg-violet-50'
      )}
      aria-busy={isPending}
    >
      <Row className="items-center gap-2">
        {isSkipped ? (
          <AlertCircle className="h-4 w-4 shrink-0 text-amber-700" />
        ) : isPending ? (
          <Loader2 className="h-4 w-4 shrink-0 animate-spin text-violet-700" />
        ) : (
          <Sparkles className="h-4 w-4 shrink-0 text-violet-700" />
        )}
        <p
          className={
            isSkipped ? 'text-sm text-amber-900' : 'text-sm text-violet-900'
          }
        >
          {title}
        </p>
        {content.success && !isPending && (
          <CheckCircle2 className="ml-auto h-4 w-4 shrink-0 text-violet-700" />
        )}
      </Row>

      {isPending && results.length > 1 && (
        <Progress
          value={progress}
          className="h-1.5 bg-violet-100"
          indicatorColor="bg-violet-500"
        />
      )}

      {results.length > 0 && !isSkipped && (
        <Row className="flex-wrap gap-2 pl-0">
          {results.map((result) => {
            const isRowPending = result.pending === true
            const urls =
              result.mockup_urls.length > 0
                ? result.mockup_urls
                : result.product_image_url
                  ? [result.product_image_url]
                  : []
            const stacked = urls.length > 1
            const canOpenGallery =
              !isRowPending && result.mockup_urls.length > 0

            return (
              <Column
                key={result.quote_sub_item_id}
                className={cn('gap-1', stacked ? 'min-w-0' : 'w-[72px]')}
              >
                <div className="relative">
                  {urls.length > 0 ? (
                    <button
                      type="button"
                      disabled={!canOpenGallery}
                      onClick={() => {
                        if (canOpenGallery) setGalleryResult(result)
                      }}
                      aria-label={t(
                        `View mockups for ${result.product_name}`,
                        `Visa mockups för ${result.product_name}`
                      )}
                      className={cn(
                        'flex items-center -space-x-2 rounded-[5px]',
                        canOpenGallery &&
                          'cursor-pointer transition-opacity hover:opacity-80'
                      )}
                    >
                      {urls.slice(0, 3).map((url, i) => (
                        <span
                          key={url}
                          className={cn(
                            'relative block h-[72px] w-[72px] overflow-hidden rounded-[5px] border border-gray-200 bg-white shadow-sm',
                            isRowPending && 'opacity-60',
                            stacked &&
                              ['-rotate-6', 'z-10 rotate-2', '-rotate-3'][i]
                          )}
                        >
                          <Image
                            src={url}
                            alt={t(result.product_name)}
                            className="h-full w-full object-cover"
                          />
                        </span>
                      ))}
                    </button>
                  ) : (
                    <div className="h-[72px] w-[72px] overflow-hidden rounded-[5px] border border-gray-200 bg-white">
                      <Skeleton className="h-full w-full border-0" />
                    </div>
                  )}
                  {isRowPending && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Loader2 className="h-5 w-5 animate-spin text-violet-700" />
                    </div>
                  )}
                </div>
                <p className="truncate text-[11px] font-medium leading-tight text-gray-900">
                  {t(result.product_name)}
                </p>
              </Column>
            )
          })}
        </Row>
      )}

      {!isPending &&
        results.some((result) => !result.success && !result.pending) && (
          <p className="text-xs text-violet-700">
            {t(
              'Some products could not be mocked up.',
              'Vissa produkter kunde inte mockas upp.'
            )}
          </p>
        )}
    </Column>
    <MockupGalleryDialog
      open={galleryResult != null}
      onOpenChange={(open) => {
        if (!open) setGalleryResult(null)
      }}
      mockupUrls={galleryResult?.mockup_urls ?? []}
      productName={galleryResult?.product_name ?? ''}
      quoteSubItemId={galleryResult?.quote_sub_item_id ?? 0}
      canGenerate={false}
      allowDelete={false}
    />
    </>
  )
}
